import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { ScanLine, CheckCircle2, AlertTriangle, XCircle, Ban } from "lucide-react";

const RESULT_VARIANTS = {
  ok: { icon: <CheckCircle2 size={36} />, color: "var(--z-success)", title: "Bienvenue !" },
  already_scanned: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)", title: "Déjà scanné" },
  unauthorized: { icon: <Ban size={36} />, color: "var(--z-error)", title: "Non autorisé" },
  invalid: { icon: <XCircle size={36} />, color: "var(--z-error)", title: "QR invalide" },
};

export default function AgentScanPage() {
  const [events, setEvents] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  const scannerRef = useRef(null);
  const scannerDivId = "qr-reader";

  useEffect(() => {
    api.get("/agent/events").then((r) => setEvents(r.data));
  }, []);

  const handleScanned = async (text) => {
    if (!text) return;
    // Stop scanner immediately to avoid burst
    await stopScanner();
    try {
      const r = await api.post("/checkin/scan", { payload: text });
      setResult(r.data);
    } catch (e) {
      setResult({ result: "invalid", message: errMsg(e) });
    }
  };

  const startScanner = async () => {
    if (scanning) return;
    setResult(null);
    setScanning(true);
    try {
      const qr = new Html5Qrcode(scannerDivId);
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        handleScanned,
        () => {}, // ignore parse errors
      );
    } catch (e) {
      setScanning(false);
      setResult({ result: "invalid", message: "Caméra indisponible. Activez les permissions ou saisissez le code manuellement." });
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scanning) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (e) { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []); // eslint-disable-line

  const submitManual = async () => {
    setShowManual(false);
    handleScanned(manual.trim());
    setManual("");
  };

  const closeResult = async () => {
    setResult(null);
  };

  return (
    <div className="z-container" data-testid="agent-scan-page">
      <h1 className="z-page-title">Scanner les billets</h1>
      <p className="z-page-sub">Présentez le QR du billet face à la caméra.</p>

      {events.length === 0 ? (
        <EmptyState
          icon={<ScanLine size={28} />}
          title="Aucun événement assigné"
          message="Demandez à votre organisateur de vous assigner à un événement."
          testId="agent-no-events"
        />
      ) : (
        <>
          <div className="z-card" style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 4 }}>Événements autorisés ({events.length})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {events.map((e) => (
                <span key={e.id} className="z-badge z-badge-teal" data-testid={`agent-event-${e.id}`}>{e.title}</span>
              ))}
            </div>
          </div>

          <div id={scannerDivId} style={{ background: "#000", minHeight: scanning ? 280 : 60, borderRadius: 16, overflow: "hidden", marginBottom: 14 }} />

          {!scanning ? (
            <ZButton onClick={startScanner} block data-testid="start-scanner-btn">
              <ScanLine size={16} /> Démarrer le scanner
            </ZButton>
          ) : (
            <ZButton variant="outline" onClick={stopScanner} block data-testid="stop-scanner-btn">
              Arrêter
            </ZButton>
          )}

          <ZButton variant="ghost" onClick={() => setShowManual(true)} block style={{ marginTop: 10 }} data-testid="manual-entry-btn">
            Saisir le code manuellement
          </ZButton>
        </>
      )}

      <Modal open={!!result} onClose={closeResult} title={result && RESULT_VARIANTS[result.result]?.title} testId="scan-result-modal">
        {result && (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: RESULT_VARIANTS[result.result]?.color, marginBottom: 14 }}>
              {RESULT_VARIANTS[result.result]?.icon}
            </div>
            <p style={{ marginBottom: 18 }} data-testid="scan-result-message">{result.message}</p>
            {result.ticket?.event?.title && (
              <div className="z-card" style={{ padding: 12, marginBottom: 14, textAlign: "left" }}>
                <div style={{ fontWeight: 600 }}>{result.ticket.event.title}</div>
                <div style={{ fontSize: 12, color: "var(--z-muted)" }}>Réf. {result.ticket.id?.slice(-8)}</div>
              </div>
            )}
            <ZButton onClick={() => { closeResult(); startScanner(); }} block data-testid="scan-continue-btn">
              Scanner le suivant
            </ZButton>
          </div>
        )}
      </Modal>

      <Modal open={showManual} onClose={() => setShowManual(false)} title="Saisie manuelle" testId="manual-modal">
        <p style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 12 }}>
          Saisissez le payload complet du QR (format ZEV|...|...|...).
        </p>
        <ZInput value={manual} onChange={(e) => setManual(e.target.value)} placeholder="ZEV|...|...|..." data-testid="manual-input" />
        <ZButton onClick={submitManual} block disabled={!manual.trim()} data-testid="manual-submit-btn">Valider</ZButton>
      </Modal>
    </div>
  );
}
